import {
  Barcode,
  GlobalProduct,
  normalizeBarcodeValue,
} from '@bofrak-backend/shared';
import Dexie, { Table } from 'dexie';
import {
  CachedGlobalProduct,
  GLOBAL_PRODUCT_CATALOG_ID,
  GlobalProductBarcodeCacheEntry,
  GlobalProductImportCandidate,
  GlobalProductImportJob,
  GlobalProductSyncMetadata,
} from './types';

const nowIso = () => new Date().toISOString();

const compareUpdatedAt = (left?: string, right?: string): number => {
  const leftTime = left ? Date.parse(left) : 0;
  const rightTime = right ? Date.parse(right) : 0;
  return leftTime - rightTime;
};

const normalizeBarcode = (barcode: Barcode): Barcode => ({
  ...barcode,
  value: normalizeBarcodeValue(barcode.value),
});

/**
 * True when the local catalog must be synced: never synced, or its last
 * successful sync is `maxAgeMs` old or more.
 */
export const isCatalogStale = (
  metadata: Pick<GlobalProductSyncMetadata, 'last_successful_sync'> | undefined,
  maxAgeMs: number,
  now = Date.now(),
): boolean => {
  const lastSync = Date.parse(metadata?.last_successful_sync ?? '');
  return !(now - lastSync < maxAgeMs);
};

export class GlobalProductDatabase extends Dexie {
  globalProducts!: Table<CachedGlobalProduct, string>;
  globalProductBarcodes!: Table<
    GlobalProductBarcodeCacheEntry,
    [string, string]
  >;
  globalProductSyncMetadata!: Table<GlobalProductSyncMetadata, string>;
  globalProductImportJobs!: Table<GlobalProductImportJob, string>;
  globalProductImportCandidates!: Table<
    GlobalProductImportCandidate,
    [string, string]
  >;

  constructor() {
    super('ShopAndSmileGlobalProductDB');

    this.version(1).stores({
      globalProducts:
        'id, approval_status, is_parent, parent_id, [approval_status+is_parent], updated_at, _cache_revision',
      globalProductBarcodes:
        '&[barcode_value+product_id], barcode_value, product_id',
      globalProductSyncMetadata:
        'id, status, revision, last_successful_sync',
      globalProductImportJobs: 'id, source, status, created_at, updated_at',
      globalProductImportCandidates:
        '&[job_id+source_product_id], job_id, family_id, status, [job_id+family_id]',
    });
  }

  private barcodeEntries(
    product: Pick<GlobalProduct, 'id' | 'barcodes'>,
  ): GlobalProductBarcodeCacheEntry[] {
    const entries = new Map<string, GlobalProductBarcodeCacheEntry>();

    for (const rawBarcode of product.barcodes ?? []) {
      const barcode = normalizeBarcode(rawBarcode);
      const value = String(barcode.value);
      if (!value) continue;
      entries.set(value, {
        barcode_value: value,
        product_id: product.id,
        barcode_type: String(barcode.type),
        description: barcode.description ?? undefined,
      });
    }

    return Array.from(entries.values());
  }

  private async nextRevision(): Promise<number> {
    const metadata = await this.globalProductSyncMetadata.get(
      GLOBAL_PRODUCT_CATALOG_ID,
    );
    return (metadata?.revision ?? 0) + 1;
  }

  async markSyncStarted(startedAt = nowIso()): Promise<void> {
    const existing = await this.globalProductSyncMetadata.get(
      GLOBAL_PRODUCT_CATALOG_ID,
    );
    await this.globalProductSyncMetadata.put({
      id: GLOBAL_PRODUCT_CATALOG_ID,
      status: 'syncing',
      revision: existing?.revision ?? 0,
      total_products: existing?.total_products ?? 0,
      last_started_at: startedAt,
      last_successful_sync: existing?.last_successful_sync,
    });
  }

  async markSyncError(error: string): Promise<void> {
    const existing = await this.globalProductSyncMetadata.get(
      GLOBAL_PRODUCT_CATALOG_ID,
    );
    await this.globalProductSyncMetadata.put({
      id: GLOBAL_PRODUCT_CATALOG_ID,
      status: 'error',
      revision: existing?.revision ?? 0,
      total_products: existing?.total_products ?? 0,
      last_started_at: existing?.last_started_at,
      last_successful_sync: existing?.last_successful_sync,
      error,
    });
  }

  async replaceCatalog(
    products: GlobalProduct[],
    syncStartedAt: number,
  ): Promise<number> {
    return this.transaction(
      'rw',
      this.globalProducts,
      this.globalProductBarcodes,
      this.globalProductSyncMetadata,
      async () => {
        const revision = await this.nextRevision();
        const incomingById = new Map(
          products.map((product) => [product.id, product]),
        );
        const existing = await this.globalProducts.toArray();
        const existingById = new Map(
          existing.map((product) => [product.id, product]),
        );
        const nextProducts: CachedGlobalProduct[] = [];

        for (const product of incomingById.values()) {
          const cached = existingById.get(product.id);
          const preserveLocal =
            cached?._local_write_at !== undefined &&
            cached._local_write_at >= syncStartedAt &&
            compareUpdatedAt(cached.updated_at, product.updated_at) > 0;

          nextProducts.push(
            preserveLocal
              ? { ...cached, _cache_revision: revision }
              : {
                  ...product,
                  barcodes: (product.barcodes ?? []).map(normalizeBarcode),
                  _cache_revision: revision,
                  _cached_at: nowIso(),
                },
          );
        }

        for (const cached of existing) {
          if (
            !incomingById.has(cached.id) &&
            cached._local_write_at !== undefined &&
            cached._local_write_at >= syncStartedAt
          ) {
            nextProducts.push({ ...cached, _cache_revision: revision });
          }
        }

        await this.globalProducts.clear();
        await this.globalProductBarcodes.clear();
        if (nextProducts.length > 0) {
          await this.globalProducts.bulkPut(nextProducts);
          const barcodeEntries = nextProducts.flatMap((product) =>
            this.barcodeEntries(product),
          );
          if (barcodeEntries.length > 0) {
            await this.globalProductBarcodes.bulkPut(barcodeEntries);
          }
        }

        await this.globalProductSyncMetadata.put({
          id: GLOBAL_PRODUCT_CATALOG_ID,
          status: 'idle',
          revision,
          total_products: nextProducts.length,
          last_started_at: new Date(syncStartedAt).toISOString(),
          last_successful_sync: nowIso(),
        });

        return revision;
      },
    );
  }

  async upsertProduct(product: GlobalProduct): Promise<void> {
    await this.transaction(
      'rw',
      this.globalProducts,
      this.globalProductBarcodes,
      this.globalProductSyncMetadata,
      async () => {
        const revision = await this.nextRevision();
        const localWriteAt = Date.now();
        await this.globalProducts.put({
          ...product,
          barcodes: (product.barcodes ?? []).map(normalizeBarcode),
          _cache_revision: revision,
          _cached_at: nowIso(),
          _local_write_at: localWriteAt,
        });
        await this.globalProductBarcodes
          .where('product_id')
          .equals(product.id)
          .delete();
        const barcodeEntries = this.barcodeEntries(product);
        if (barcodeEntries.length > 0) {
          await this.globalProductBarcodes.bulkPut(barcodeEntries);
        }

        const metadata = await this.globalProductSyncMetadata.get(
          GLOBAL_PRODUCT_CATALOG_ID,
        );
        await this.globalProductSyncMetadata.put({
          id: GLOBAL_PRODUCT_CATALOG_ID,
          status: metadata?.status ?? 'idle',
          revision,
          total_products: await this.globalProducts.count(),
          last_started_at: metadata?.last_started_at,
          last_successful_sync: metadata?.last_successful_sync,
          error: metadata?.error,
        });
      },
    );
  }

  async removeProduct(productId: string): Promise<void> {
    await this.transaction(
      'rw',
      this.globalProducts,
      this.globalProductBarcodes,
      this.globalProductSyncMetadata,
      async () => {
        const revision = await this.nextRevision();
        await this.globalProducts.delete(productId);
        await this.globalProductBarcodes
          .where('product_id')
          .equals(productId)
          .delete();
        const metadata = await this.globalProductSyncMetadata.get(
          GLOBAL_PRODUCT_CATALOG_ID,
        );
        await this.globalProductSyncMetadata.put({
          id: GLOBAL_PRODUCT_CATALOG_ID,
          status: metadata?.status ?? 'idle',
          revision,
          total_products: await this.globalProducts.count(),
          last_started_at: metadata?.last_started_at,
          last_successful_sync: metadata?.last_successful_sync,
          error: metadata?.error,
        });
      },
    );
  }

  async findOwnersByBarcodes(
    barcodeValues: Array<string | number>,
  ): Promise<GlobalProduct[]> {
    const values = Array.from(
      new Set(barcodeValues.map(normalizeBarcodeValue).filter(Boolean)),
    );
    if (values.length === 0) return [];

    const entries = await this.globalProductBarcodes
      .where('barcode_value')
      .anyOf(values)
      .toArray();
    const productIds = Array.from(
      new Set(entries.map((entry) => entry.product_id)),
    );
    const products = await this.globalProducts.bulkGet(productIds);
    return products.filter(Boolean) as CachedGlobalProduct[];
  }

  async putImportJob(
    job: GlobalProductImportJob,
    candidates: GlobalProductImportCandidate[],
  ): Promise<void> {
    await this.transaction(
      'rw',
      this.globalProductImportJobs,
      this.globalProductImportCandidates,
      async () => {
        await this.globalProductImportJobs.put(job);
        if (candidates.length > 0) {
          await this.globalProductImportCandidates.bulkPut(candidates);
        }
      },
    );
  }

  async updateImportCandidate(
    jobId: string,
    sourceProductId: string,
    patch: Partial<GlobalProductImportCandidate>,
  ): Promise<void> {
    await this.globalProductImportCandidates.update(
      [jobId, sourceProductId],
      { ...patch, updated_at: nowIso() },
    );
  }
}

export const globalProductDb = new GlobalProductDatabase();